// WorkspaceSection — a Card with icon, title, description, and a collapsible two-column
// field grid. The building block of the workspace form layout.

import { useState, type ReactNode } from 'react';
import { Box, Card, CardContent, Collapse, IconButton, Stack, Typography } from '@mui/material';
import { ArrowDown2 } from 'iconsax-reactjs';
import { UI } from '../brand/ui';

export function WorkspaceSection({
  title,
  description,
  defaultExpanded = true,
  children
}: {
  title: string;
  description?: string;
  defaultExpanded?: boolean;
  children: ReactNode;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Card sx={{ transition: (theme) => theme.transitions.create(['box-shadow', 'transform'], { duration: 200 }), '&:hover': { transform: 'translateY(-1px)' } }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Section header — clickable to collapse */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => setExpanded((e) => !e)}
          sx={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: UI.ink }}>
              {title}
            </Typography>
            {description ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {description}
              </Typography>
            ) : null}
          </Box>
          <IconButton size="small" sx={{ transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: (theme) => theme.transitions.create('transform', { duration: 200 }) }}>
            <ArrowDown2 size={18} />
          </IconButton>
        </Stack>

        {/* Collapsible field grid */}
        <Collapse in={expanded} sx={{ transition: (theme) => theme.transitions.create(['opacity', 'max-height'], { duration: 200 }) }}>
          <Box sx={{ mt: 2 }}>{children}</Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

// Two-column responsive grid for fields within a section.
// Checkbox and subform fields span full width (they set gridColumn: '1 / -1').
export function WorkspaceFieldGrid({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 2,
        alignItems: 'start'
      }}
    >
      {children}
    </Box>
  );
}
